// src/oura/newscan/scanDraftNotice.ts
//
// What staff are told once the New scan flow leaves its page.
//
//   scanDraftStarted     the Start step's half: the notice the page hands to
//                        the screen it lands on, and the scan toast.
//   useScanDraftNotice   the landing screen's half: read the notice from the
//                        router state once, show it, then clear it so a
//                        refresh or Back does not show it again.
//
// The New scan page's own snackbar unmounts when it navigates away, so a
// notice rides along as router state `{ scanDraftNotice: string }`. The
// snackbar is only the "you left" channel: the Scanning tag on the businesses
// list and the business page (useScanningSites) is what stays.

import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { rehydrateScanJobs } from "@oura/aeo";
import { OURA_ROOT } from "@oura/nav";
import type { ScanDraftKind, ScanDraftPurpose, StartScanDraftResult } from "@service/aeo/scanDraftTypes";
import { FIRST_CHECK_MINUTES } from "@oura/onboarding/derive";
import { FIRST_CHECK } from "./copy";
import { SCAN_DRAFT_UI_ON, scanDraftMode } from "./flags";

/** Router state the New scan page navigates away with. */
export interface ScanDraftNoticeState {
    scanDraftNotice: string;
}

/** One duration everywhere (JOURNEY-SPEC §2.5 "One duration"): FIRST_CHECK_MINUTES, never the server's estimate. */
function minutesText(): string {
    return `About ${FIRST_CHECK_MINUTES} minutes.`;
}

/** How the landing screen shows a notice: longer for a started scan, with Open when the business is known. */
export interface ScanDraftNoticeOptions {
    duration?: number;
    /** Where Open goes. Absent: no Open. */
    to?: string;
}

/** A started-scan notice stays up 10 s, not the snackbar's 5. */
const STARTED_MS = 10000;

/*
 * The router state carries the notice text only (the flow's close() passes a
 * string), so the started notice's extras wait here for the screen that shows
 * that same text. Same tab, same app: nothing outlives a reload.
 */
let pendingStarted: { text: string; opts: ScanDraftNoticeOptions } | null = null;

/**
 * After the Start step's scan has started: the notice for the screen it lands
 * on, naming the business. On the live backend the run is registered in the
 * scan job store (rehydrateScanJobs reads the in-flight runs), so the shared
 * scan toast follows it; a First AI check too, which the old onboarding start
 * never was. The fixtures start nothing real, so they skip it.
 */
export function scanDraftStarted(
    result: StartScanDraftResult,
    ctx: { kind: ScanDraftKind; purpose?: ScanDraftPurpose | null; domain: string; businessName?: string },
): ScanDraftNoticeState {
    if (scanDraftMode() === "live") void rehydrateScanJobs();
    const firstCheck = ctx.purpose === "first_check";
    const what = firstCheck
        ? FIRST_CHECK.started
        : ctx.kind === "new_scan"
          ? `Scan started for ${ctx.businessName?.trim() || ctx.domain}.`
          : "Re-scan started on the new set.";
    // Applied at the next monthly check (H2-c): nothing runs now, so no minutes. Taking over the pitch (H4-e): its run, not a new one.
    const when = result.scheduledFor ? new Date(result.scheduledFor) : null;
    const day = when && !Number.isNaN(when.getTime()) ? `${when.getDate()} ${when.toLocaleString("en-GB", { month: "short" })} ${when.getFullYear()}` : "";
    const text = result.reused
        ? "This set had already started a scan."
        : result.scheduledFor
          ? `The new set waits for the monthly check on ${day}. Nothing runs before then.`
          : result.tookOverPitch
            ? "The pitch scan is now the first AI check. It finishes when the pitch does."
            : `${what} ${minutesText()}`;
    // A First AI check lands on onboarding; Open would only reopen it.
    const to = result.businessId && !firstCheck ? `${OURA_ROOT}/businesses/${encodeURIComponent(result.businessId)}` : undefined;
    pendingStarted = { text, opts: { duration: STARTED_MS, to } };
    return { scanDraftNotice: text };
}

function noticeOf(state: unknown): string | null {
    if (!state || typeof state !== "object") return null;
    const v = (state as { scanDraftNotice?: unknown }).scanDraftNotice;
    return typeof v === "string" && v.trim() ? v : null;
}

/**
 * The landing screen shows a notice the New scan page left in the router
 * state, once, then takes it out of the history entry (other state kept).
 * Does nothing with the New scan switch off.
 */
export function useScanDraftNotice(show: (text: string, opts?: ScanDraftNoticeOptions) => void): void {
    const location = useLocation();
    const navigate = useNavigate();
    // The history entry's key: StrictMode runs the effect twice, a notice shows once.
    const shown = useRef<string | null>(null);

    useEffect(() => {
        if (!SCAN_DRAFT_UI_ON) return;
        const notice = noticeOf(location.state);
        if (!notice || shown.current === location.key) return;
        shown.current = location.key;
        const extra = pendingStarted?.text === notice ? pendingStarted.opts : undefined;
        pendingStarted = null;
        // Open is left out on the business it would open.
        const to = extra?.to && extra.to !== location.pathname ? extra.to : undefined;
        show(notice, extra ? { duration: extra.duration, to } : undefined);
        const rest = { ...(location.state as Record<string, unknown>) };
        delete rest.scanDraftNotice;
        navigate(
            { pathname: location.pathname, search: location.search, hash: location.hash },
            { replace: true, state: Object.keys(rest).length ? rest : null },
        );
    }, [location, navigate, show]);
}
